import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Col, Row, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import copy from 'copy-to-clipboard';

import useDocumentSize from '../../../../hooks/useDocumentSize';
import { CheckoutContext } from '../../shared/CheckoutProvider';
import qrPix from '../../../../img/qrPix.png';
import { countPix } from '../../shared/constants';
import Cupom from '../Cupom';
import '../style/pix.scss';
import { verifyPixPayment } from '../../../../redux/api/checkout';

const POLLING_INTERVAL = 10000;

const TERMINAL_ORDER_STATUSES = ['DENIED', 'ERROR', 'CANCELLED'];

// `deadline` já é um instante do relógio local, calculado a partir da duração
// que a Getnet informou — por isso não há comparação com horário de servidor.
const secondsUntil = (deadline) => {
  if (!deadline) return 0;

  const remaining = deadline - Date.now();

  return remaining > 0 ? Math.floor(remaining / 1000) : 0;
};

const PaymentPix = ({ onFinish }) => {
  const useSize = useDocumentSize();
  const keyPix = useRef(null);

  const {
    callBack,
    course,
    processLoading,
    setPaymentPixStatus,
    setChangeTypeCredit,
    setMethod,
    resetPixCallback,
  } = useContext(CheckoutContext);

  const deadline = callBack?.callback?.deadline;
  const orderId = callBack?.order?.id_order;
  const hasQrCode = Boolean(callBack?.status && callBack?.callback?.pixChave);

  // O contador vem do prazo real do QR Code, e não de um decremento a partir
  // da montagem: assim o tempo continua correto depois de um reload e não
  // deriva com a aba em segundo plano.
  const [remaining, setRemaining] = useState(() =>
    deadline ? secondsUntil(deadline) : Math.floor(countPix / 1000)
  );
  // Encerramento informado pelo backend (negado, cancelado ou expirado lá).
  // Precisa ser sticky: o tick seguinte recalcularia `remaining` pelo prazo
  // local, que ainda está no futuro, e o QR voltaria a aparecer.
  const [backendStatus, setBackendStatus] = useState(null);

  useEffect(() => {
    setBackendStatus(null);
  }, [orderId]);

  useEffect(() => {
    if (!hasQrCode || backendStatus) return;

    setRemaining(secondsUntil(deadline));

    const interval = setInterval(() => {
      setRemaining(secondsUntil(deadline));
    }, 1000);

    return () => clearInterval(interval);
  }, [backendStatus, deadline, hasQrCode]);

  const terminated = TERMINAL_ORDER_STATUSES.includes(backendStatus);
  const expired = hasQrCode && (Boolean(backendStatus) || remaining <= 0);

  useEffect(() => {
    if (!hasQrCode || expired || !orderId) return;

    const interval = setInterval(async () => {
      const returnPix = await verifyPixPayment(orderId);

      if (returnPix?.status) {
        setPaymentPixStatus('paid');
        return;
      }

      // O backend informa quando o pedido morreu (negado pelo webhook da
      // Getnet, cancelado ou expirado), sem depender do contador local.
      if (returnPix?.expired) {
        setBackendStatus(returnPix?.order_status || 'EXPIRED');
        setRemaining(0);
      }
    }, POLLING_INTERVAL);

    return () => clearInterval(interval);
  }, [expired, hasQrCode, orderId, setPaymentPixStatus]);

  const renderCount = useMemo(() => {
    const min = Math.floor(remaining / 60);
    const sec = remaining % 60;

    return `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  }, [remaining]);

  const copyCode = () => {
    copy(callBack?.callback?.pixChave);
  };

  const handleQRCode = () => {
    onFinish();
  };

  const handleGenerateNewPix = useCallback(() => {
    setBackendStatus(null);
    resetPixCallback();
    onFinish();
  }, [onFinish, resetPixCallback]);

  const handlePayWithCreditCard = useCallback(() => {
    setBackendStatus(null);
    resetPixCallback();
    setChangeTypeCredit('credit');
    setMethod('credit');
  }, [resetPixCallback, setChangeTypeCredit, setMethod]);

  if (!hasQrCode) {
    return (
      <Cupom>
        <Button
          className="btn-button btn-generate-pix"
          onClick={handleQRCode}
          disabled={processLoading}
        >
          <div className="btn-content-process">
            {!processLoading ? (
              <span className="btn-content-process-text">Gerar código Pix</span>
            ) : (
              <>
                <Spin indicator={<LoadingOutlined spin />} />
                <span className="btn-content-process-text">
                  Gerando seu código Pix...
                </span>
              </>
            )}
          </div>
        </Button>
        {processLoading && (
          <p className="pix-generating-hint">
            Isso pode levar alguns segundos. Não feche nem recarregue esta página.
          </p>
        )}
      </Cupom>
    );
  }

  return (
    <>
      <Row>
        <div className="container-method-payment">
          <Col className="gutter-row" xs={24} md={12}>
            <div className="card-pix">
              {useSize.width >= 767 && (
                <div className="card-body-pix">
                  <p className="title-card-pix">Pague com a chave pix</p>
                  <input
                    className={`text-copy ${expired ? 'text-copy-disabled' : ''}`}
                    disabled={!expired}
                    ref={keyPix}
                    value={callBack?.callback?.pixChave}
                  />
                </div>
              )}

              <div className="card-body-pix">
                <button
                  className={`button-card-pix ${expired ? 'button-card-pix-disabled' : ''}`}
                  onClick={expired ? undefined : copyCode}
                >
                  Copiar código pix
                </button>
                {!expired && (
                  <>
                    <p className="time-cod-pix">Seu código expira em:</p>
                    <p className="time-cod-pix-num">{renderCount}</p>
                  </>
                )}
              </div>
            </div>
          </Col>
          {useSize.width >= 768 && (
            <div>
              <div className="card-pix">
                <Row className="card-body-pix">
                  <p className="title-card-pix-qr">
                    Ou aponte a câmera do seu celular para o <strong> QR Code </strong> para realizar o pagamento.
                  </p>
                  {!expired ? (
                    <img
                      src={`data:image/png;base64,${callBack?.callback?.qrCode}`}
                      alt="qr code pix"
                      width="60%"
                    />
                  ) : (
                    <img src={qrPix} className="pix-disabled" alt="qr code pix" />
                  )}
                </Row>
              </div>
            </div>
          )}
        </div>

        <Col xs={24} md={24} lg={24}>
          {expired ? (
            <div className="pix-expired-area">
              <p className="pix-expired-title">
                {terminated
                  ? 'Seu pagamento por Pix não foi concluído'
                  : 'Seu código Pix expirou'}
              </p>
              <p className="pix-expired-text">
                Você pode gerar um novo código ou pagar com cartão de crédito.
              </p>
              <div className="pix-expired-actions">
                <Button
                  className="btn-button btn-generate-pix"
                  onClick={handleGenerateNewPix}
                  disabled={processLoading}
                >
                  <div className="btn-content-process">
                    {!processLoading ? (
                      <span className="btn-content-process-text">
                        Gerar novo código Pix
                      </span>
                    ) : (
                      <>
                        <Spin indicator={<LoadingOutlined spin />} />
                        <span className="btn-content-process-text">
                          Gerando seu código Pix...
                        </span>
                      </>
                    )}
                  </div>
                </Button>
                <Button
                  className="btn-button btn-pix-to-credit"
                  onClick={handlePayWithCreditCard}
                >
                  Pagar com cartão de crédito
                </Button>
              </div>
            </div>
          ) : (
            <div className="pix-instructions">
              <p className="pix-instructions-title">Como pagar:</p>
              <ol className="pix-instructions-list">
                <li>Abra o aplicativo do seu banco</li>
                <li>Escolha Pix e a opção de ler QR Code ou colar o código</li>
                <li>Confira o valor e confirme o pagamento</li>
              </ol>
              <p className="pix-instructions-status">
                Estamos verificando seu pagamento automaticamente. Não feche esta página.
              </p>
              <Button
                className="btn-button btn-pix-to-credit"
                onClick={handlePayWithCreditCard}
              >
                Prefiro pagar com cartão de crédito
              </Button>
            </div>
          )}
        </Col>

        <Col xs={24} md={24} lg={24}>
          <div className="card-checkout-info">
            <div className="card-checkout-info-inner">
              <p>
                Após o pagamento esta será sua estreia no curso {course.curso}!
              </p>
              <br/>
              <p>As informações de login serão enviadas para o seu email cadastrado!</p>
              <br/>
              <p>
                O seu login será o seu e-mail cadastrado e sua senha corresponde ao seu CPF (somente os números).
              </p>
              <br/>
              <p>
                Você também receberá estas informações por e-mail. Caso a mensagem não esteja na sua caixa de entrada, verifique a sua pasta de spam.
              </p>
              <br/>
              <p>
                Em caso de dúvidas entre em contato pelo nosso <span>WhatsApp (51) 98194-1625</span>
              </p>
            </div>
          </div>
        </Col>
      </Row>
    </>
  );
};

export default PaymentPix;
